import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type Variant =
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'danger'
    | 'accent'
    | 'outline';

type Size = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<Variant, string> = {
    primary:
        'bg-brand-600 text-white hover:bg-brand-700 shadow-sm shadow-brand-600/20',

    accent:
        'bg-accent-600 text-white hover:bg-accent-700 shadow-sm shadow-accent-600/20',

    secondary:
        'bg-white text-ink border border-line-strong hover:bg-surface-sunken',

    ghost:
        'text-ink-soft hover:bg-surface-sunken hover:text-ink',

    danger:
        'bg-critical text-white hover:opacity-90',

    outline:
        'border border-line-strong bg-transparent text-ink hover:bg-surface-sunken',
};

const SIZES: Record<Size, string> = {
    sm: 'h-9 px-3.5 text-sm gap-1.5',
    md: 'h-11 px-5 text-sm gap-2',
    lg: 'h-13 px-7 text-base gap-2.5',
    icon: 'h-10 w-10 p-0',
};

const BASE =
    'inline-flex items-center justify-center rounded-lg font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap';

function classesFor(
    variant: Variant,
    size: Size,
    className?: string,
): string {
    return cn(
        BASE,
        VARIANTS[variant],
        SIZES[size],
        className,
    );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: Variant;
    size?: Size;
    loading?: boolean;
};

export function Button({
    variant = 'primary',
    size = 'md',
    loading = false,
    className,
    children,
    disabled,
    ...props
}: ButtonProps) {
    return (
        <button
            {...props}
            disabled={disabled || loading}
            className={classesFor(variant, size, className)}
            aria-busy={loading}
        >
            {loading && (
                <Loader2
                    className="size-4 animate-spin"
                    aria-hidden
                />
            )}

            {children}
        </button>
    );
}

type ButtonLinkProps = {
    to: string;
    variant?: Variant;
    size?: Size;
    className?: string;
    children: ReactNode;
} & Omit<
    ComponentProps<typeof Link>,
    'to' | 'className' | 'children'
>;

export function ButtonLink({
    to,
    variant = 'primary',
    size = 'md',
    className,
    children,
    ...props
}: ButtonLinkProps) {
    return (
        <Link
            to={to}
            className={classesFor(
                variant,
                size,
                className,
            )}
            {...props}
        >
            {children}
        </Link>
    );
}